// src/app/shared/fees-summary-comparison/fees-summary.service.ts
import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import { CostComparison } from '../../models/cost-comparison/cost-comparison.model';

@Injectable({
    providedIn: 'root'
})
export class FeesSummaryService {

    /**
     * Collects all processor fee details from cost comparisons (same pattern as fixFees in component)
     */
    fixFees(costComparisonObjects: Array<CostComparison>): Array<any> {
        const processorFees: Array<any> = [];
        costComparisonObjects.forEach(ccObj => {
            ccObj.comparisonFeeGroupings?.processor?.fees?.forEach(f => {
                if (f.feeDetails) {
                    f.feeDetails.forEach(fd => {
                        const nfd = {
                            ...fd,
                            isFeeDetail: true,
                            parentFeeLabel: f.label
                        };
                        processorFees.push(nfd);
                    });
                }
            });
        });
        return processorFees;
    }

    /**
     * Calculates the statistical median of a numeric array
     */
    private median(values: number[]): number {
        if (!values.length) return 0;
        const sorted = [...values].sort((a, b) => a - b);
        const mid = Math.floor(sorted.length / 2);
        return sorted.length % 2 !== 0
            ? sorted[mid]
            : (sorted[mid - 1] + sorted[mid]) / 2;
    }

    /**
     * Gets the total monthly fees for a single cost comparison
     */
    getCurrentTotalMonthlyFeesSummary(ccObj: CostComparison): number {
        const fg = ccObj.comparisonFeeGroupings;
        if (fg == null) {
            throw new Error('Comparison fee groupings are required');
        }
        return fg.processor.totalAmount +
            fg.additional.totalAmount +
            fg.assessment.totalAmount +
            fg.nonNegotiable.totalAmount +
            fg.interchange.totalAmount;
    }

    /**
     * Gets the proposed total monthly fees for a single cost comparison
     */
    getProposedTotalMonthlyFeesSummary(ccObj: CostComparison): number {
        const fg = ccObj.comparisonFeeGroupings;
        if (fg == null) {
            throw new Error('Comparison fee groupings are required');
        }
        return fg.processor.proposedTotalAmount +
            fg.additional.proposedTotalAmount +
            fg.assessment.proposedTotalAmount +
            fg.nonNegotiable.proposedTotalAmount +
            fg.interchange.proposedTotalAmount;
    }

    /**
     * Calculates average current total monthly fees across all comparisons
     */
    getSummaryAverageCurrentTotalMonthlyFees(costComparisonObjects: Array<CostComparison>): number {
        const currentTotalMonthlyFees = costComparisonObjects.map(ccObj => this.getCurrentTotalMonthlyFeesSummary(ccObj));
        const sum = _.sum(currentTotalMonthlyFees);
        const average = sum / currentTotalMonthlyFees.length;

        return _.round(average, 2);
    }

    /**
     * Calculates median current total monthly fees across all comparisons
     */
    getSummaryMedianCurrentTotalMonthlyFees(costComparisonObjects: Array<CostComparison>): number {
        const currentTotalMonthlyFees = costComparisonObjects.map(ccObj => this.getCurrentTotalMonthlyFeesSummary(ccObj));
        return _.round(this.median(currentTotalMonthlyFees), 2);
    }

    /**
     * Calculates average proposed total monthly fees across all comparisons
     */
    getSummaryAverageProposedTotalFees(costComparisonObjects: Array<CostComparison>): number {
        const proposedTotalMonthlyFees = costComparisonObjects.map(ccObj => this.getProposedTotalMonthlyFeesSummary(ccObj));
        const sum = _.sum(proposedTotalMonthlyFees);
        const average = sum / proposedTotalMonthlyFees.length;
        return average;
    }

    /**
     * Calculates median proposed total monthly fees across all comparisons
     */
    getSummaryMedianProposedTotalFees(costComparisonObjects: Array<CostComparison>): number {
        const proposedTotalMonthlyFees = costComparisonObjects.map(ccObj => this.getProposedTotalMonthlyFeesSummary(ccObj));
        return this.median(proposedTotalMonthlyFees);
    }

    /**
     * Calculates average current effective rate across all comparisons
     */
    getCurrentEffectiveRateSummary(costComparisonObjects: Array<CostComparison>): number {
        const currentEffectiveRates = costComparisonObjects.map(ccObj => {
            const fg = ccObj.comparisonFeeGroupings;
            if (fg == null) return NaN;
            return this.getCurrentTotalMonthlyFeesSummary(ccObj) / fg.processor.totalVolume;
        });
        // Remove Infinity, -Infinity, and NaN values
        const filteredCurrentEffectiveRates = currentEffectiveRates.filter(value => isFinite(value));

        const currentEffectiveRateSum = _.sum(filteredCurrentEffectiveRates);
        const average = currentEffectiveRateSum / filteredCurrentEffectiveRates.length;

        return average;
    }

    /**
     * Calculates median current effective rate across all comparisons
     */
    getCurrentEffectiveRateMedian(costComparisonObjects: Array<CostComparison>): number {
        const currentEffectiveRates = costComparisonObjects.map(ccObj => {
            const fg = ccObj.comparisonFeeGroupings;
            if (fg == null) return NaN;
            return this.getCurrentTotalMonthlyFeesSummary(ccObj) / fg.processor.totalVolume;
        });
        const filteredCurrentEffectiveRates = currentEffectiveRates.filter(value => isFinite(value));
        return this.median(filteredCurrentEffectiveRates);
    }

    /**
     * Calculates average proposed effective rate across all comparisons
     */
    getProposedEffectiveRateSummary(costComparisonObjects: Array<CostComparison>): number {
        const proposedEffectiveRates = costComparisonObjects.map(ccObj => {
            const fg = ccObj.comparisonFeeGroupings;
            if (fg == null) return NaN;
            return this.getProposedTotalMonthlyFeesSummary(ccObj) / fg.processor.totalVolume;
        });
        // Remove Infinity, -Infinity, and NaN values
        const filteredProposedEffectiveRates = proposedEffectiveRates.filter(value => isFinite(value));

        const proposedEffectiveRateSum = _.sum(filteredProposedEffectiveRates);
        const average = proposedEffectiveRateSum / filteredProposedEffectiveRates.length;

        return average;
    }

    /**
     * Calculates median proposed effective rate across all comparisons
     */
    getProposedEffectiveRateMedian(costComparisonObjects: Array<CostComparison>): number {
        const proposedEffectiveRates = costComparisonObjects.map(ccObj => {
            const fg = ccObj.comparisonFeeGroupings;
            if (fg == null) return NaN;
            return this.getProposedTotalMonthlyFeesSummary(ccObj) / fg.processor.totalVolume;
        });
        const filteredProposedEffectiveRates = proposedEffectiveRates.filter(value => isFinite(value));
        return this.median(filteredProposedEffectiveRates);
    }

    /**
     * Calculates average monthly savings across all comparisons
     */
    getSavingsTotalMonthlyFees(costComparisonObjects: Array<CostComparison>): number {
        const savingsTotalMonthlyFees = costComparisonObjects.map(ccObj => {
            const fg = ccObj.comparisonFeeGroupings;
            if (fg == null) return 0;
            return fg.processor.totalAmount - fg.processor.proposedTotalAmount +
                fg.additional.totalAmount - fg.additional.proposedTotalAmount +
                fg.assessment.totalAmount - fg.assessment.proposedTotalAmount +
                fg.nonNegotiable.totalAmount - fg.nonNegotiable.proposedTotalAmount +
                fg.interchange.totalAmount - fg.interchange.proposedTotalAmount;
        });
        const sum = _.sum(savingsTotalMonthlyFees);
        const average = sum / savingsTotalMonthlyFees.length;
        return _.round(average, 2);
    }

    /**
     * Calculates median monthly savings across all comparisons
     */
    getSavingsTotalMonthlyFeesMedian(costComparisonObjects: Array<CostComparison>): number {
        const savingsTotalMonthlyFees = costComparisonObjects.map(ccObj => {
            const fg = ccObj.comparisonFeeGroupings;
            if (fg == null) return 0;
            return fg.processor.totalAmount - fg.processor.proposedTotalAmount +
                fg.additional.totalAmount - fg.additional.proposedTotalAmount +
                fg.assessment.totalAmount - fg.assessment.proposedTotalAmount +
                fg.nonNegotiable.totalAmount - fg.nonNegotiable.proposedTotalAmount +
                fg.interchange.totalAmount - fg.interchange.proposedTotalAmount;
        });
        return _.round(this.median(savingsTotalMonthlyFees), 2);
    }
}